import React, { useCallback, useEffect, useRef, useState } from "react";

const STYLE_ID = "pagetone-mini-player-inline-styles";
const DRAG_THRESHOLD_PX = 6;
const VIEWPORT_EDGE_GAP_PX = 12;

function injectStyles() {
  if (typeof document === "undefined" || document.getElementById(STYLE_ID)) {
    return;
  }

  const element = document.createElement("style");
  element.id = STYLE_ID;
  element.textContent = `
    .ptmp-root * {
      box-sizing: border-box;
    }

    .ptmp-card {
      position: fixed;
      left: 50%;
      z-index: 1200;
      width: min(560px, calc(100vw - 32px));
      padding: 14px 16px 12px;
      border-radius: 18px;
      background: color-mix(in srgb, var(--echo-bg-surface, #ffffff) 50%, transparent);
      color: var(--echo-text-primary, #2C2B29);
      border: 1px solid color-mix(in srgb, var(--echo-border-light, rgba(0,0,0,0.06)) 50%, rgba(255,255,255,0.46) 50%);
      box-shadow: 0 18px 44px rgba(24, 28, 41, 0.1);
      backdrop-filter: blur(18px) saturate(120%);
      -webkit-backdrop-filter: blur(18px) saturate(120%);
      touch-action: none;
      user-select: none;
      transition: box-shadow 0.18s ease, opacity 0.18s ease, transform 0.18s ease;
    }

    .ptmp-card.is-embedded {
      position: relative;
      left: auto;
      top: auto;
      bottom: auto;
      width: 100%;
      margin: 0 auto;
      border-radius: 20px;
      transform: none !important;
      cursor: default;
      touch-action: auto;
    }

    .ptmp-card.is-dragging {
      box-shadow: 0 24px 54px rgba(24, 28, 41, 0.2);
      transition: none;
      cursor: grabbing;
    }

    .ptmp-card.is-minimizing {
      opacity: 0;
      transform: translate(-50%, -10px) scale(0.92);
      pointer-events: none;
    }

    .ptmp-minimize {
      position: absolute;
      top: 8px;
      right: 10px;
      width: 22px;
      height: 22px;
      padding: 0;
      border: 0;
      border-radius: 999px;
      background: rgba(25, 36, 67, 0.18);
      color: var(--echo-text-secondary, #6B6A65);
      font-size: 14px;
      font-weight: 700;
      line-height: 1;
      cursor: pointer;
    }

    .ptmp-row1,
    .ptmp-row2,
    .ptmp-row3 {
      display: flex;
      align-items: center;
    }

    .ptmp-row1 {
      gap: 12px;
      margin-bottom: 10px;
      padding-right: 26px;
    }

    .ptmp-row2 {
      gap: 8px;
      margin-bottom: 10px;
      justify-content: center;
    }

    .ptmp-row3 {
      gap: 12px;
    }

    .ptmp-cover-btn,
    .ptmp-control,
    .ptmp-play {
      border: 0;
      background: transparent;
      padding: 0;
      color: inherit;
      cursor: pointer;
      appearance: none;
    }

    .ptmp-cover,
    .ptmp-cover-fallback {
      width: 46px;
      height: 46px;
      border-radius: 10px;
      flex: 0 0 auto;
      display: block;
      box-shadow: 0 10px 20px rgba(17, 24, 39, 0.16);
    }

    .ptmp-cover {
      object-fit: cover;
    }

    .ptmp-cover-fallback {
      display: grid;
      place-items: center;
      background: linear-gradient(145deg, rgba(166, 93, 75, 0.9), rgba(61, 86, 126, 0.9));
      color: white;
      font-size: 20px;
      font-weight: 700;
      letter-spacing: 0.04em;
    }

    .ptmp-meta {
      min-width: 0;
      flex: 1;
    }

    .ptmp-meta.is-clickable {
      cursor: pointer;
    }

    .ptmp-title,
    .ptmp-subtitle {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .ptmp-title {
      font-size: 13px;
      font-weight: 700;
      color: var(--echo-text-primary, #2C2B29);
    }

    .ptmp-subtitle {
      margin-top: 3px;
      font-size: 11px;
      color: var(--echo-text-secondary, #6B6A65);
    }

    .ptmp-subtitle.is-resume {
      color: color-mix(in srgb, var(--echo-accent, #A65D4B) 84%, white 16%);
      font-weight: 700;
    }

    .ptmp-wave {
      display: inline-flex;
      align-items: flex-end;
      gap: 3px;
      height: 18px;
      margin-left: auto;
      margin-right: 2px;
    }

    .ptmp-wave-bar {
      width: 3px;
      height: 100%;
      border-radius: 999px;
      background: color-mix(in srgb, var(--echo-accent, #A65D4B) 70%, rgba(61, 86, 126, 0.85) 30%);
      animation: ptmp-wave-bounce 1s ease-in-out infinite;
      transform-origin: center bottom;
    }

    .ptmp-wave-bar:nth-child(2) { animation-delay: 0.12s; }
    .ptmp-wave-bar:nth-child(3) { animation-delay: 0.24s; }
    .ptmp-wave-bar:nth-child(4) { animation-delay: 0.36s; }
    .ptmp-wave-bar:nth-child(5) { animation-delay: 0.48s; }

    .ptmp-wave.is-paused .ptmp-wave-bar {
      animation-play-state: paused;
      opacity: 0.5;
      transform: scaleY(0.45);
    }

    @keyframes ptmp-wave-bounce {
      0%, 100% { transform: scaleY(0.32); }
      50% { transform: scaleY(1); }
    }

    .ptmp-control,
    .ptmp-play {
      min-width: 48px;
      height: 38px;
      padding: 0 14px;
      border-radius: 999px;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.01em;
    }

    .ptmp-control {
      background: rgba(61, 86, 126, 0.22);
      color: var(--echo-text-secondary, #6B6A65);
      border: 1px solid rgba(61, 86, 126, 0.2);
    }

    .ptmp-play {
      min-width: 92px;
      background: color-mix(in srgb, var(--echo-accent, #A65D4B) 82%, transparent);
      color: white;
      box-shadow: 0 10px 18px rgba(166, 93, 75, 0.24);
    }

    .ptmp-play.is-resume {
      background: color-mix(in srgb, var(--echo-accent, #A65D4B) 74%, rgba(61, 86, 126, 0.9) 26%);
    }

    .ptmp-track-wrap {
      flex: 1;
      min-width: 0;
    }

    .ptmp-track {
      position: relative;
      width: 100%;
      height: 8px;
      border-radius: 999px;
      background: rgba(61, 86, 126, 0.12);
      border: 1px solid rgba(61, 86, 126, 0.08);
      overflow: hidden;
      cursor: pointer;
      touch-action: none;
    }

    .ptmp-track.is-disabled {
      cursor: default;
      opacity: 0.7;
    }

    .ptmp-track-fill {
      position: absolute;
      inset: 0 auto 0 0;
      height: 100%;
      border-radius: inherit;
      background: linear-gradient(90deg, rgba(166, 93, 75, 0.95), rgba(61, 86, 126, 0.95));
    }

    .ptmp-track-thumb {
      position: absolute;
      top: 50%;
      width: 14px;
      height: 14px;
      border-radius: 999px;
      background: white;
      border: 2px solid var(--echo-accent, #A65D4B);
      box-shadow: 0 2px 8px rgba(24, 28, 41, 0.18);
      transform: translate(-50%, -50%);
      pointer-events: none;
    }

    .ptmp-times {
      margin-top: 6px;
      display: flex;
      justify-content: space-between;
      gap: 12px;
      font-size: 11px;
      color: var(--echo-text-muted, #A8A6A1);
      font-variant-numeric: tabular-nums;
    }

    @media (max-width: 768px) {
      .ptmp-card {
        width: min(420px, calc(100vw - 20px));
        padding: 12px 12px 10px;
      }

      .ptmp-row1 {
        gap: 10px;
        margin-bottom: 8px;
      }

      .ptmp-row2 {
        gap: 6px;
      }

      .ptmp-control,
      .ptmp-play {
        min-width: 42px;
        height: 34px;
        padding: 0 11px;
      }

      .ptmp-play {
        min-width: 82px;
      }
    }
  `;

  document.head.appendChild(element);
}

function formatTime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return "0:00";
  }

  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

function getInitials(title) {
  const raw = String(title || "P").trim();
  return raw ? raw.slice(0, 1).toUpperCase() : "P";
}

function Waveform({ isPlaying }) {
  return (
    <span className={`ptmp-wave ${isPlaying ? "" : "is-paused"}`} aria-hidden>
      {[1, 2, 3, 4, 5].map((index) => (
        <span key={index} className="ptmp-wave-bar" />
      ))}
    </span>
  );
}

function Cover({ src, title, onClick }) {
  const [errored, setErrored] = useState(false);

  if (src && !errored) {
    return (
      <button className="ptmp-cover-btn" type="button" onClick={onClick} aria-label="Open PageTone">
        <img
          className="ptmp-cover"
          src={src}
          alt={title || "Book cover"}
          onError={() => setErrored(true)}
          draggable={false}
        />
      </button>
    );
  }

  return (
    <button className="ptmp-cover-btn" type="button" onClick={onClick} aria-label="Open PageTone">
      <span className="ptmp-cover-fallback">{getInitials(title)}</span>
    </button>
  );
}

function SeekBar({
  currentTime,
  duration,
  progressPercent,
  seekEnabled,
  onSeekPercent
}) {
  const trackRef = useRef(null);
  const dragPointerRef = useRef(null);

  const safePercent = Number.isFinite(progressPercent)
    ? Math.max(0, Math.min(100, progressPercent))
    : 0;

  const getPercentFromClientX = useCallback((clientX) => {
    const rect = trackRef.current?.getBoundingClientRect();

    if (!rect?.width) {
      return null;
    }

    return Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
  }, []);

  const commitSeek = useCallback(
    (nextPercent) => {
      if (!seekEnabled || !Number.isFinite(nextPercent)) {
        return;
      }

      onSeekPercent?.(nextPercent);
    },
    [onSeekPercent, seekEnabled]
  );

  const handlePointerDown = useCallback(
    (event) => {
      if (!seekEnabled || (event.pointerType === "mouse" && event.button !== 0)) {
        return;
      }

      event.stopPropagation();
      const nextPercent = getPercentFromClientX(event.clientX);

      if (nextPercent == null) {
        return;
      }

      dragPointerRef.current = event.pointerId;
      commitSeek(nextPercent);

      try {
        trackRef.current?.setPointerCapture?.(event.pointerId);
      } catch (error) {
        // Ignore unsupported pointer capture.
      }
    },
    [commitSeek, getPercentFromClientX, seekEnabled]
  );

  const handlePointerMove = useCallback(
    (event) => {
      if (!seekEnabled || dragPointerRef.current !== event.pointerId) {
        return;
      }

      event.stopPropagation();
      const nextPercent = getPercentFromClientX(event.clientX);

      if (nextPercent != null) {
        commitSeek(nextPercent);
      }
    },
    [commitSeek, getPercentFromClientX, seekEnabled]
  );

  const handlePointerEnd = useCallback(
    (event) => {
      if (!seekEnabled || dragPointerRef.current !== event.pointerId) {
        return;
      }

      event.stopPropagation();

      try {
        trackRef.current?.releasePointerCapture?.(event.pointerId);
      } catch (error) {
        // Ignore unsupported pointer capture release.
      }

      dragPointerRef.current = null;
    },
    [seekEnabled]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (!seekEnabled) {
        return;
      }

      let nextPercent = safePercent;

      switch (event.key) {
        case "ArrowLeft":
        case "ArrowDown":
          nextPercent -= 5;
          break;
        case "ArrowRight":
        case "ArrowUp":
          nextPercent += 5;
          break;
        case "Home":
          nextPercent = 0;
          break;
        case "End":
          nextPercent = 100;
          break;
        default:
          return;
      }

      event.preventDefault();
      commitSeek(nextPercent);
    },
    [commitSeek, safePercent, seekEnabled]
  );

  return (
    <div className="ptmp-track-wrap">
      <div
        ref={trackRef}
        className={`ptmp-track ${seekEnabled ? "" : "is-disabled"}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        onKeyDown={handleKeyDown}
        role={seekEnabled ? "slider" : undefined}
        tabIndex={seekEnabled ? 0 : undefined}
        aria-label={seekEnabled ? "Playback progress" : undefined}
        aria-valuemin={seekEnabled ? 0 : undefined}
        aria-valuemax={seekEnabled ? 100 : undefined}
        aria-valuenow={seekEnabled ? Math.round(safePercent) : undefined}
        aria-valuetext={
          seekEnabled ? `${formatTime(currentTime)} / ${formatTime(duration)}` : undefined
        }
      >
        <div className="ptmp-track-fill" style={{ width: `${safePercent}%` }} />
        <div className="ptmp-track-thumb" style={{ left: `${safePercent}%` }} />
      </div>
      <div className="ptmp-times">
        <span>{formatTime(currentTime)}</span>
        <span>{formatTime(duration)}</span>
      </div>
    </div>
  );
}

export default function EchoMiniPlayer({
  visible,
  placement = "top",
  book,
  chapterTitle,
  resumePending = false,
  resumeLabel = "",
  resumeNote = "",
  progress = 0,
  currentTime = 0,
  duration = 0,
  isPlaying,
  onTogglePlay,
  onOpenEcho,
  onSeekPercent,
  onSkipBack,
  onSkipForward,
  minimizable = false,
  onMinimize
}) {
  injectStyles();

  const containerRef = useRef(null);
  const dragStateRef = useRef(null);
  const suppressClickRef = useRef(false);
  const [dragOffsets, setDragOffsets] = useState({ top: 0, bottom: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isMinimizing, setIsMinimizing] = useState(false);
  const [topBaseOffset, setTopBaseOffset] = useState(() => {
    if (typeof window === "undefined") {
      return 126;
    }

    if (window.innerWidth <= 768) {
      return 116;
    }

    if (window.innerWidth <= 1180) {
      return 122;
    }

    return 126;
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const updateTopBaseOffset = () => {
      if (window.innerWidth <= 768) {
        setTopBaseOffset(116);
        return;
      }

      if (window.innerWidth <= 1180) {
        setTopBaseOffset(122);
        return;
      }

      setTopBaseOffset(126);
    };

    updateTopBaseOffset();
    window.addEventListener("resize", updateTopBaseOffset);

    return () => {
      window.removeEventListener("resize", updateTopBaseOffset);
    };
  }, []);

  useEffect(() => {
    if (visible) {
      setIsMinimizing(false);
    }
  }, [visible]);

  useEffect(() => {
    const currentContainer = containerRef.current;

    return () => {
      if (dragStateRef.current?.pointerId != null) {
        try {
          currentContainer?.releasePointerCapture?.(dragStateRef.current.pointerId);
        } catch (error) {
          // Ignore pointer capture release errors during unmount.
        }
      }
    };
  }, []);

  if (!visible || !book) {
    return null;
  }

  const isEmbedded = placement === "embedded";
  const isTopPlacement = placement === "top";
  const currentOffsetY = dragOffsets[placement] || 0;
  const metaOpensEcho = !isTopPlacement && !isEmbedded;
  const canSeek = typeof onSeekPercent === "function";
  const actionLabel =
    !isPlaying && resumePending
      ? resumeLabel || "Resume"
      : isPlaying
      ? "Pause"
      : "Play";
  const subtitleLabel =
    !isPlaying && resumePending
      ? resumeNote || chapterTitle || "Resume when you return"
      : chapterTitle || "Ready to listen";
  const normalizedProgress =
    duration > 0
      ? Math.max(0, Math.min(100, (currentTime / duration) * 100))
      : progress > 1
      ? Math.max(0, Math.min(100, progress))
      : Math.max(0, Math.min(100, progress * 100));

  function updateDragOffset(nextOffset) {
    setDragOffsets((current) => ({
      ...current,
      [placement]: nextOffset
    }));
  }

  function handlePointerDown(event) {
    if (isEmbedded) {
      return;
    }

    if (event.pointerType === "mouse" && event.button !== 0) {
      return;
    }

    if (event.target.closest("button, [role='slider']")) {
      return;
    }

    const rect = containerRef.current?.getBoundingClientRect?.();

    if (!rect) {
      return;
    }

    dragStateRef.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startTop: rect.top,
      startOffset: currentOffsetY,
      height: rect.height,
      didDrag: false
    };

    suppressClickRef.current = false;

    try {
      containerRef.current?.setPointerCapture?.(event.pointerId);
    } catch (error) {
      // Ignore unsupported pointer capture.
    }
  }

  function handlePointerMove(event) {
    if (isEmbedded) {
      return;
    }

    const dragState = dragStateRef.current;

    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    const deltaY = event.clientY - dragState.startY;

    if (!dragState.didDrag && Math.abs(deltaY) < DRAG_THRESHOLD_PX) {
      return;
    }

    if (!dragState.didDrag) {
      dragState.didDrag = true;
      suppressClickRef.current = true;
      setIsDragging(true);
    }

    const viewportHeight = window.innerHeight || 0;
    const minTop = VIEWPORT_EDGE_GAP_PX;
    const maxTop = Math.max(
      VIEWPORT_EDGE_GAP_PX,
      viewportHeight - dragState.height - VIEWPORT_EDGE_GAP_PX
    );
    const desiredTop = dragState.startTop + deltaY;
    const clampedTop = Math.max(minTop, Math.min(maxTop, desiredTop));
    const nextOffset = dragState.startOffset + (clampedTop - dragState.startTop);

    updateDragOffset(nextOffset);
  }

  function handlePointerEnd(event) {
    if (isEmbedded) {
      return;
    }

    const dragState = dragStateRef.current;

    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    if (dragState.didDrag) {
      suppressClickRef.current = true;
      window.setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
    }

    try {
      containerRef.current?.releasePointerCapture?.(event.pointerId);
    } catch (error) {
      // Ignore unsupported pointer capture release.
    }

    dragStateRef.current = null;
    setIsDragging(false);
  }

  function runAction(action) {
    if (suppressClickRef.current) {
      return;
    }

    action?.();
  }

  function handleMinimizeClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (isMinimizing || typeof onMinimize !== "function") {
      return;
    }

    setIsMinimizing(true);
    window.setTimeout(() => {
      onMinimize();
      setIsMinimizing(false);
    }, 180);
  }

  return (
    <div className="ptmp-root">
      <div
        ref={containerRef}
        className={`ptmp-card ${isEmbedded ? "is-embedded" : ""} ${isDragging ? "is-dragging" : ""} ${
          isMinimizing ? "is-minimizing" : ""
        }`}
        role="region"
        aria-label="PageTone mini player"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        style={{
          top: isTopPlacement ? `${topBaseOffset}px` : undefined,
          bottom: isTopPlacement || isEmbedded ? "auto" : `${VIEWPORT_EDGE_GAP_PX}px`,
          transform: isEmbedded ? undefined : `translate(-50%, ${currentOffsetY}px)`
        }}
      >
        {minimizable ? (
          <button
            className="ptmp-minimize"
            type="button"
            aria-label="Hide mini player"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={handleMinimizeClick}
          >
            -
          </button>
        ) : null}

        <div className="ptmp-row1">
          <Cover src={book.cover} title={book.title || "PageTone"} onClick={() => runAction(onOpenEcho)} />
          {metaOpensEcho ? (
            <button
              className="ptmp-meta is-clickable"
              type="button"
              onClick={() => runAction(onOpenEcho)}
            >
              <div className="ptmp-title">{book.title || "Untitled book"}</div>
              <div className={`ptmp-subtitle ${resumePending ? "is-resume" : ""}`}>
                {subtitleLabel}
              </div>
            </button>
          ) : (
            <div className="ptmp-meta">
              <div className="ptmp-title">{book.title || "Untitled book"}</div>
              <div className={`ptmp-subtitle ${resumePending ? "is-resume" : ""}`}>
                {subtitleLabel}
              </div>
            </div>
          )}
          <Waveform isPlaying={isPlaying} />
        </div>

        <div className="ptmp-row2">
          <button
            className="ptmp-control"
            type="button"
            onClick={() => runAction(onSkipBack)}
            aria-label="Back 15 seconds"
          >
            -15
          </button>
          <button
            className={`ptmp-play ${resumePending && !isPlaying ? "is-resume" : ""}`}
            type="button"
            onClick={() => runAction(onTogglePlay)}
            aria-label={actionLabel}
          >
            {actionLabel}
          </button>
          <button
            className="ptmp-control"
            type="button"
            onClick={() => runAction(onSkipForward)}
            aria-label="Forward 15 seconds"
          >
            +15
          </button>
        </div>

        <div className="ptmp-row3">
          <SeekBar
            currentTime={currentTime}
            duration={duration}
            progressPercent={normalizedProgress}
            seekEnabled={canSeek}
            onSeekPercent={onSeekPercent}
          />
        </div>
      </div>
    </div>
  );
}
